import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Drawer, Spin, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import config from '../../config';
import usePermissions from '../../hooks/usePermissions';
import { useIsDesktop, NdoeImage } from '../../components/ndoe';
import VentesClienteModal from './VentesClienteModal';
import './dashboardSecretaire.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const ZONE = 'Africa/Kinshasa';

// Jour « boutique » (Kinshasa) — même mécanique que le cockpit mobile : les
// bornes de période envoyées à l'API sont TOUJOURS des dates explicites, jamais
// dépendantes du fuseau du téléphone ou du serveur.
const kinshasaDay = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const v = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
  return `${v.year}-${v.month}-${v.day}`;
};
const addDays = (day, delta) => {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
};

const PRESETS = [
  { key: 'today', label: "Aujourd'hui", range: () => { const j = kinshasaDay(); return { start: j, end: j }; } },
  { key: 'd1', label: 'Hier', range: () => { const j = addDays(kinshasaDay(), -1); return { start: j, end: j }; } },
  { key: 'd2', label: 'J-2', range: () => { const j = addDays(kinshasaDay(), -2); return { start: j, end: j }; } },
  { key: 'd3', label: 'J-3', range: () => { const j = addDays(kinshasaDay(), -3); return { start: j, end: j }; } },
  { key: 'custom', label: 'Personnalisé', range: null },
];

const nf = (v) => Number(v || 0).toLocaleString('fr-FR');
const usd = (v) => `${nf(Math.round(Number(v || 0)))} $`;
const clock = (v) => (v ? new Intl.DateTimeFormat('fr-FR', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' }).format(new Date(v)) : '—');

// Variation vs période précédente : rendue seulement quand elle apporte
// une information (base non nulle).
const Delta = ({ cur, prev }) => {
  const c = Number(cur || 0); const p = Number(prev || 0);
  if (!p) return null;
  const pct = Math.round(((c - p) / p) * 100);
  return <em className={`secdash-delta ${pct >= 0 ? 'is-up' : 'is-down'}`}>{pct >= 0 ? '+' : ''}{pct}% vs période préc.</em>;
};

const NIVEAU_CLASS = { rouge: 'is-rouge', orange: 'is-orange', info: 'is-info' };

// Libellés des statuts de créance (creanceStatus.js, rendus tels quels par l'API).
const CREANCE_LABELS = {
  avoir: 'Avoir', soldee: 'Soldée', non_echue: 'Non échue', echeance_proche: 'Échéance proche',
  echue: 'Échue', en_retard: 'En retard', peu_sure: 'Peu sûre', irrecouvrable: 'Irrécouvrable', en_cours: 'En cours',
};

export default function DashboardSecretaire() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();

  // Période portée par l'URL (préservée au retour d'un détail / bouton back).
  const preset = PRESETS.some((p) => p.key === searchParams.get('p')) ? searchParams.get('p') : 'today';
  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');
  const range = useMemo(() => {
    if (preset === 'custom' && urlStart && urlEnd) return { start: urlStart, end: urlEnd };
    const found = PRESETS.find((p) => p.key === preset && p.range);
    return (found || PRESETS[0]).range();
  }, [preset, urlStart, urlEnd]);

  const setPreset = useCallback((key, custom) => {
    const next = new URLSearchParams(searchParams);
    next.set('p', key);
    if (key === 'custom' && custom) { next.set('start', custom.start); next.set('end', custom.end); }
    else { next.delete('start'); next.delete('end'); }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [livreurOuvert, setLivreurOuvert] = useState(null);
  const [timelineId, setTimelineId] = useState('');
  const [timeline, setTimeline] = useState(null);      // { loading, data, error }
  const [tri, setTri] = useState({ cle: 'paires_confiees', sens: -1 });
  // Drill-down recettes (20/07) : clic sur une cliente → détail des achats.
  const [drillCliente, setDrillCliente] = useState(null);   // { id_client, client }

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${DOMAIN}/api/dashboard/secretaire`, { params: { start: range.start, end: range.end } });
      setData(r.data); setUpdatedAt(new Date());
    } catch (e) {
      setError(e.response?.status === 403
        ? 'Accès non autorisé à ce tableau de bord.'
        : (e.response?.data?.error || e.response?.data?.message || 'Erreur de chargement.'));
    } finally { setLoading(false); }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const ouvrirTimeline = useCallback(async (id) => {
    const num = Number(id);
    if (!Number.isInteger(num) || num <= 0) return;
    setTimeline({ loading: true });
    try {
      const r = await axios.get(`${DOMAIN}/api/commande/timeline/${num}`);
      setTimeline({ loading: false, data: r.data });
    } catch (e) {
      setTimeline({ loading: false, error: e.response?.data?.error || 'Chronologie indisponible.' });
    }
  }, []);

  // La finance est décidée par l'API (payload.finance présent ou non) ; le hook
  // front sert aux libellés/boutons pour ne rien promettre d'interdit.
  const finance = data?.finance || null;
  const showFinance = !!finance && can('creances.voir');

  const livreursTries = useMemo(() => {
    const rows = [...(data?.par_livreur || [])];
    rows.sort((a, b) => (Number(b[tri.cle] || 0) - Number(a[tri.cle] || 0)) * tri.sens * -1
      || String(a.nom).localeCompare(String(b.nom)));
    return rows;
  }, [data, tri]);
  const trierPar = (cle) => setTri((t) => ({ cle, sens: t.cle === cle ? -t.sens : -1 }));
  const fleche = (cle) => (tri.cle === cle ? (tri.sens === -1 ? ' ↓' : ' ↑') : '');

  const cmd = data?.commandes || {};
  const paires = data?.paires || {};
  const comp = data?.comparaison || {};
  const alertes = data?.alertes || [];
  const durees = data?.durees || {};
  const repartition = data?.repartition || [];
  const totalRep = repartition.reduce((s, r) => s + Number(r.count || 0), 0) || 1;

  const customStart = urlStart || range.start;
  const customEnd = urlEnd || range.end;

  return (
    <div className="secdash-root">
      {/* ── En-tête : titre, périodes, actualisation ─────────────────────── */}
      <header className="secdash-header">
        <div>
          <h1>Supervision — Secrétaire</h1>
          <p className="secdash-sub">
            Période du {range.start}{range.end !== range.start ? ` au ${range.end}` : ''} ·
            {' '}MAJ {updatedAt ? clock(updatedAt) : '…'}
          </p>
        </div>
        <div className="secdash-header-actions">
          <div className="secdash-periods" role="tablist" aria-label="Période">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" role="tab" aria-selected={preset === p.key}
                className={preset === p.key ? 'is-active' : ''}
                onClick={() => (p.key === 'custom' ? setPreset('custom', { start: customStart, end: customEnd }) : setPreset(p.key))}>
                {p.label}
              </button>
            ))}
          </div>
          <button type="button" className="secdash-refresh" onClick={load} disabled={loading} aria-label="Actualiser">
            <ReloadOutlined spin={loading} />
          </button>
        </div>
      </header>
      {preset === 'custom' && (
        <div className="secdash-custom-range">
          <label>Du <input type="date" value={customStart} max={kinshasaDay()}
            onChange={(e) => setPreset('custom', { start: e.target.value, end: customEnd })} /></label>
          <label>au <input type="date" value={customEnd} max={kinshasaDay()}
            onChange={(e) => setPreset('custom', { start: customStart, end: e.target.value })} /></label>
        </div>
      )}

      {error && <div className="secdash-error" role="alert">⚠ {error} <button type="button" onClick={load}>Réessayer</button></div>}
      {loading && !data && <div className="secdash-loading"><Spin size="large" /></div>}

      {data && <>
        {/* ── Rangée 1 : indicateurs critiques (cartes cliquables) ────────── */}
        <div className="secdash-cards">
          <button type="button" className="secdash-card" onClick={() => navigate('/listeCommande')}>
            <small>Commandes ({data.periode.jours > 1 ? `${data.periode.jours} j` : 'jour'})</small>
            <strong>{nf(cmd.total)}</strong>
            <span>{nf(cmd.livrees + (cmd.livrees_partielles || 0))} livrée(s) · {nf(cmd.affectees_non_prises)} non prise(s) · <b className={cmd.non_affectees > 0 ? 'is-warn' : ''}>{nf(cmd.non_affectees)} sans livreur</b></span>
            <Delta cur={cmd.total} prev={comp.commandes_total} />
          </button>
          <button type="button" className="secdash-card" onClick={() => navigate('/livraison')}>
            <small>Paires</small>
            <strong>{nf(paires.commandees)}<i>commandées</i></strong>
            <span>{nf(paires.confiees)} confiées · {nf(paires.vendues)} vendues · {nf(paires.retours_client + paires.retours_livraison)} retour(s)</span>
            <Delta cur={paires.vendues} prev={comp.paires_vendues} />
          </button>
          {showFinance ? (
            <button type="button" className="secdash-card secdash-card--finance" onClick={() => navigate('/creances')}>
              <small>Montants (période)</small>
              <strong>{usd(finance.vendu)}<i>vendus</i></strong>
              <span>Attendu {usd(finance.attendu)} · Encaissé {usd(finance.encaisse_net)}</span>
              <span>Créances ouvertes {usd(finance.credits_ouverts)}{finance.avoirs > 0 ? ` · Avoirs ${usd(finance.avoirs)}` : ''}</span>
              <Delta cur={finance.vendu} prev={comp.vendu} />
            </button>
          ) : (
            <div className="secdash-card is-muted" aria-hidden="true">
              <small>Montants</small>
              <strong>—</strong>
              <span>Accès financier non accordé</span>
            </div>
          )}
          <button type="button" className={`secdash-card ${alertes.length ? 'secdash-card--alerte' : ''}`}
            onClick={() => document.getElementById('secdash-alertes')?.scrollIntoView({ behavior: 'smooth' })}>
            <small>Alertes</small>
            <strong>{alertes.length}</strong>
            <span>{alertes[0] ? alertes[0].message : 'Rien à signaler ✓'}</span>
          </button>
        </div>

        {/* ── Rangée 2 : répartition des commandes par statut ─────────────── */}
        <section className="secdash-section">
          <h2>Commandes de la période par statut</h2>
          {repartition.length === 0
            ? <p className="secdash-empty">Aucune commande sur la période.</p>
            : <div className="secdash-repartition">
                {repartition.map((r) => (
                  <button key={r.code} type="button" className="secdash-rep-row" onClick={() => navigate('/listeCommande')}>
                    <Tag color={r.tone === 'default' ? undefined : r.tone}>{r.label}</Tag>
                    <div className="secdash-rep-bar-wrap"><div className={`secdash-rep-bar tone-${r.tone}`} style={{ width: `${Math.max(4, Math.round((r.count / totalRep) * 100))}%` }} /></div>
                    <b>{nf(r.count)}</b>
                  </button>
                ))}
                {cmd.ventes_directes > 0 && <p className="secdash-note">+ {nf(cmd.ventes_directes)} vente(s) directe(s) au comptoir (hors circuit livraison)</p>}
              </div>}
        </section>

        {/* ── Rangée 3 : activité des livreurs ─────────────────────────────── */}
        <section className="secdash-section">
          <h2>Par livreur — activité de la période</h2>
          {livreursTries.length === 0
            ? <p className="secdash-empty">Aucune activité livreur sur la période.</p>
            : isDesktop ? (
              <div className="secdash-table-wrap">
                <table className="secdash-table">
                  <thead><tr>
                    <th>Livreur</th>
                    <th onClick={() => trierPar('affectees')} className="is-sortable">Affectées{fleche('affectees')}</th>
                    <th>Prises</th>
                    <th onClick={() => trierPar('paires_confiees')} className="is-sortable">Paires conf.{fleche('paires_confiees')}</th>
                    <th onClick={() => trierPar('paires_vendues')} className="is-sortable">Vendues{fleche('paires_vendues')}</th>
                    <th>Retours</th>
                    <th onClick={() => trierPar('en_circulation')} className="is-sortable">Dehors{fleche('en_circulation')}</th>
                    {showFinance && <>
                      <th onClick={() => trierPar('montant_vendu')} className="is-sortable">Vendu{fleche('montant_vendu')}</th>
                      <th onClick={() => trierPar('encaisse')} className="is-sortable">Encaissé{fleche('encaisse')}</th>
                      <th onClick={() => trierPar('ecart')} className="is-sortable">Écart{fleche('ecart')}</th>
                    </>}
                  </tr></thead>
                  <tbody>
                    {livreursTries.map((l) => (
                      <tr key={l.id} onClick={() => setLivreurOuvert(l)} tabIndex={0} role="button">
                        <td className="secdash-liv-nom">{l.nom}</td>
                        <td>{nf(l.affectees)}</td>
                        <td>{nf(l.prises)}{l.non_prises > 0 ? <em className="is-warn"> ({nf(l.non_prises)} refus)</em> : ''}</td>
                        <td>{nf(l.paires_confiees)}</td>
                        <td>{nf(l.paires_vendues)}</td>
                        <td>{nf(l.retours_paires)}</td>
                        <td>{nf(l.en_circulation)}</td>
                        {showFinance && <>
                          <td>{usd(l.montant_vendu)}</td>
                          <td>{usd(l.encaisse)}</td>
                          <td>{l.ecart > 0 ? <Tag color="orange">{usd(l.ecart)}</Tag> : <Tag color="green">OK</Tag>}</td>
                        </>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="secdash-liv-cards">
                {livreursTries.map((l) => (
                  <button key={l.id} type="button" className="secdash-liv-card" onClick={() => setLivreurOuvert(l)}>
                    <div className="secdash-liv-head"><strong>{l.nom}</strong>
                      {showFinance && (l.ecart > 0 ? <Tag color="orange">écart {usd(l.ecart)}</Tag> : <Tag color="green">OK</Tag>)}
                    </div>
                    <span>{nf(l.affectees)} affectée(s) · {nf(l.prises)} prise(s) · {nf(l.paires_confiees)} paires · {nf(l.paires_vendues)} vendue(s) · {nf(l.en_circulation)} dehors</span>
                    {showFinance && <span>Vendu {usd(l.montant_vendu)} · Encaissé {usd(l.encaisse)}</span>}
                  </button>
                ))}
              </div>
            )}
        </section>

        {/* ── Rangée 4 : vue financière ────────────────────────────────────── */}
        {showFinance && (
          <section className="secdash-section">
            <h2>Finance — attendu · vendu · encaissé</h2>
            <div className="secdash-finance-bars">
              {(() => {
                const max = Math.max(finance.attendu, finance.vendu, finance.encaisse_net, 1);
                const bar = (label, val, cls) => (
                  <div className="secdash-fin-row" key={label}>
                    <span>{label}</span>
                    <div className="secdash-fin-bar-wrap"><div className={`secdash-fin-bar ${cls}`} style={{ width: `${Math.max(2, Math.round((val / max) * 100))}%` }} /></div>
                    <b>{usd(val)}</b>
                  </div>
                );
                return <>
                  {bar('Attendu (commandé)', finance.attendu, 'is-attendu')}
                  {bar('Vendu (confirmé)', finance.vendu, 'is-vendu')}
                  {bar('Encaissé (net)', finance.encaisse_net, 'is-encaisse')}
                </>;
              })()}
            </div>
            <div className="secdash-fin-chips">
              {(finance.par_moyen || []).map((m) => <Tag key={m.moyen}>{m.moyen === 'NON_PRECISE' ? 'Espèces (livreur)' : m.moyen} : {usd(m.montant)}</Tag>)}
            </div>
            {finance.creances_statuts && (
              <div className="secdash-fin-chips">
                <span className="secdash-note">Créances :</span>
                {Object.entries(finance.creances_statuts).map(([s, n]) => (
                  <Tag key={s} color={['echue', 'en_retard', 'peu_sure', 'irrecouvrable'].includes(s) ? 'red' : s === 'avoir' ? 'purple' : undefined}
                    onClick={() => navigate('/creances')} style={{ cursor: 'pointer' }}>
                    {CREANCE_LABELS[s] || s} : {nf(n)}
                  </Tag>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Recettes par cliente (demande proprio 18/07) : ventes CONFIRMÉES
            vs revenus attendus sur la période, comme sur le tableau de bord
            admin. Financier → visible seulement avec creances.voir. ────────── */}
        {showFinance && data?.recettes_clientes && (data.recettes_clientes.items || []).length > 0 && (
          <section className="secdash-section" id="secdash-recettes">
            <h2>Recettes par cliente — ventes confirmées vs attendu</h2>
            <div className="secdash-recettes-meta">
              {data.recettes_clientes.nb_clientes} cliente(s) · période choisie · cliquez une cliente pour le détail
              {data.recettes_clientes.items.length < data.recettes_clientes.nb_clientes
                && ` · ${data.recettes_clientes.items.length} affichées (les plus fortes ventes) — le Total porte sur toutes`}
            </div>
            <div className="secdash-recettes-wrap">
              <table className="secdash-recettes">
                <thead>
                  <tr><th>Cliente</th><th>Paires</th><th>Ventes confirmées</th><th>Attendu</th><th>Écart</th></tr>
                </thead>
                <tbody>
                  {data.recettes_clientes.items.map((c) => (
                    <tr key={c.id_client} className={c.id_client ? "secdash-recettes-ligne" : undefined}
                      onClick={() => c.id_client && setDrillCliente({ id_client: c.id_client, client: c.client })}>
                      <td className="secdash-recettes-nom">{c.client}</td>
                      <td data-col="Paires">{nf(c.paires)}</td>
                      <td className="secdash-recettes-ok" data-col="Ventes confirmées">{usd(c.ventes)}</td>
                      <td data-col="Attendu">{usd(c.attendu)}</td>
                      <td data-col="Écart" className={c.ecart > 0 ? 'secdash-recettes-reste' : ''}>{usd(c.ecart)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="secdash-recettes-nom"><b>Total ({data.recettes_clientes.nb_clientes})</b></td>
                    <td data-col="Paires"><b>{nf(data.recettes_clientes.totaux.paires)}</b></td>
                    <td className="secdash-recettes-ok" data-col="Ventes confirmées"><b>{usd(data.recettes_clientes.totaux.ventes)}</b></td>
                    <td data-col="Attendu"><b>{usd(data.recettes_clientes.totaux.attendu)}</b></td>
                    <td data-col="Écart" className={data.recettes_clientes.totaux.ecart > 0 ? 'secdash-recettes-reste' : ''}><b>{usd(data.recettes_clientes.totaux.ecart)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        {/* ── Rangée 5 : alertes + durées + chronologie ───────────────────── */}
        {/* Stock restant des variantes vendues sur la période (demande proprio
            15/07) : après ses ventes, la secrétaire voit ce qu'il reste —
            disponible = source canonique serveur (réservations déduites). */}
        {Array.isArray(data?.variantes_vendues) && (
          <section className="secdash-section" id="secdash-stock-vendues">
            <h2>Stock des variantes vendues — période</h2>
            {data.variantes_vendues.length === 0
              ? <p className="secdash-empty">Aucune vente sur la période.</p>
              : <div className="secdash-stockv-wrap">
                  <table className="secdash-stockv">
                    <thead>
                      <tr><th>Variante</th><th>Vendues</th><th>Stock</th><th>Disponible</th></tr>
                    </thead>
                    <tbody>
                      {data.variantes_vendues.map((v) => (
                        <tr key={v.id_varianteProduit}>
                          <td className="secdash-stockv-nom">
                            <NdoeImage src={v.img} alt={v.nom_produit || 'variante'} size="sm" />
                            <span>
                              <b>{v.nom_produit || 'Produit'}</b>
                              <small>{v.code_produit || ''}{v.taille ? ` · P${v.taille}` : ''}</small>
                            </span>
                          </td>
                          {/* data-col : sur téléphone, le tableau devient une liste
                              de cartes (CSS) et chaque valeur porte alors son
                              libellé — sinon « 1 · 0 · Épuisé » ne voudrait rien
                              dire une fois les en-têtes de colonne disparus. */}
                          <td data-col="Vendues"><b>{nf(v.vendues)}</b></td>
                          <td data-col="Stock">{nf(v.stock_physique)}</td>
                          <td data-col="Disponible">
                            {v.disponible <= 0
                              ? <Tag color="red">Épuisé</Tag>
                              : v.disponible <= 2
                                ? <Tag color="orange">{nf(v.disponible)} — bas</Tag>
                                : <Tag color="green">{nf(v.disponible)}</Tag>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </section>
        )}

        <section className="secdash-section" id="secdash-alertes">
          <h2>Alertes — état actuel (toutes périodes)</h2>
          {alertes.length === 0
            ? <p className="secdash-empty">Aucune alerte ✓</p>
            : <div className="secdash-alertes">
                {alertes.map((a) => (
                  <button key={a.code} type="button" className={`secdash-alerte ${NIVEAU_CLASS[a.niveau] || 'is-info'}`}
                    onClick={() => a.link && navigate(a.link)} disabled={!a.link}>
                    <b>{nf(a.count)}</b>
                    <span className="secdash-alerte-msg">{a.message}</span>
                    <span className="secdash-alerte-action">{a.action} →</span>
                  </button>
                ))}
              </div>}
          <div className="secdash-durees">
            <div><small>Affectation → prise en charge</small><strong>{durees.affectation_pec_h == null ? '—' : `${durees.affectation_pec_h} h`}</strong></div>
            <div><small>Prise en charge → résultat</small><strong>{durees.pec_resultat_h == null ? '—' : `${durees.pec_resultat_h} h`}</strong></div>
            <div><small>Taux de livraison</small><strong>{durees.taux_livraison_pct == null ? '—' : `${durees.taux_livraison_pct} %`}</strong></div>
          </div>
          <div className="secdash-timeline-lookup">
            <label htmlFor="secdash-tl">Chronologie d'une commande</label>
            <input id="secdash-tl" type="number" min="1" placeholder="N° commande" value={timelineId}
              onChange={(e) => setTimelineId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ouvrirTimeline(timelineId); }} />
            <button type="button" disabled={!timelineId} onClick={() => ouvrirTimeline(timelineId)}>Voir</button>
          </div>
        </section>
      </>}

      {/* ── Drawer : détail livreur ────────────────────────────────────────── */}
      <Drawer open={!!livreurOuvert} onClose={() => setLivreurOuvert(null)}
        title={livreurOuvert ? `Livreur — ${livreurOuvert.nom}` : ''}
        width={isDesktop ? 420 : '100%'} destroyOnClose>
        {livreurOuvert && (
          <div className="secdash-drawer">
            <dl>
              <div><dt>Commandes affectées</dt><dd>{nf(livreurOuvert.affectees)}</dd></div>
              <div><dt>Prises en charge</dt><dd>{nf(livreurOuvert.prises)}</dd></div>
              <div><dt>Non prises (refus)</dt><dd>{nf(livreurOuvert.non_prises)}</dd></div>
              <div><dt>Commandes livrées</dt><dd>{nf(livreurOuvert.livrees)}</dd></div>
              <div><dt>Paires confiées</dt><dd>{nf(livreurOuvert.paires_confiees)}</dd></div>
              <div><dt>Paires vendues</dt><dd>{nf(livreurOuvert.paires_vendues)}</dd></div>
              <div><dt>Paires retournées</dt><dd>{nf(livreurOuvert.retours_paires)}</dd></div>
              <div><dt>Encore en circulation</dt><dd>{nf(livreurOuvert.en_circulation)}</dd></div>
              {showFinance && <>
                <div><dt>Montant vendu</dt><dd>{usd(livreurOuvert.montant_vendu)}</dd></div>
                <div><dt>Encaissé (net)</dt><dd>{usd(livreurOuvert.encaisse)}</dd></div>
                <div><dt>Écart indicatif</dt><dd>{livreurOuvert.ecart > 0
                  ? <Tag color="orange">{usd(livreurOuvert.ecart)} — crédit accordé OU encaissé au bureau</Tag>
                  : <Tag color="green">Aucun</Tag>}</dd></div>
              </>}
              {livreurOuvert.dernier_evenement && (
                <div><dt>Dernier événement</dt><dd>{livreurOuvert.dernier_evenement.action} · {livreurOuvert.dernier_evenement.date}</dd></div>
              )}
            </dl>
            <button type="button" className="secdash-drawer-link"
              onClick={() => navigate('/supervision-livraisons')}>
              Ouvrir la supervision des livraisons →
            </button>
          </div>
        )}
      </Drawer>

      {/* ── Drawer : chronologie d'une commande ───────────────────────────── */}
      <Drawer open={!!timeline} onClose={() => setTimeline(null)}
        title={timeline?.data ? `Commande #${timeline.data.id_commande}${timeline.data.client ? ` — ${timeline.data.client}` : ''}` : 'Chronologie'}
        width={isDesktop ? 480 : '100%'} destroyOnClose>
        {timeline?.loading && <div className="secdash-loading"><Spin /></div>}
        {timeline?.error && <p className="secdash-error">⚠ {timeline.error}</p>}
        {timeline?.data && (
          <div className="secdash-timeline">
            {timeline.data.evenements.map((e, i) => (
              <div key={i} className={`secdash-tl-item is-${e.type}`}>
                <span className="secdash-tl-time">
                  {new Intl.DateTimeFormat('fr-FR', { timeZone: ZONE, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(e.date))}
                </span>
                <div className="secdash-tl-body">
                  <strong>{e.libelle}{e.montant != null ? ` · ${usd(e.montant)}` : ''}</strong>
                  {(e.detail || e.acteur || e.livreur) && (
                    <small>{[e.detail, e.acteur && `par ${e.acteur}`, e.livreur && e.livreur !== e.acteur ? `livreur ${e.livreur}` : null].filter(Boolean).join(' · ')}</small>
                  )}
                  {e.depuis_precedent_min != null && e.depuis_precedent_min > 0 && (
                    <small className="secdash-tl-gap">+{e.depuis_precedent_min >= 60 ? `${Math.round(e.depuis_precedent_min / 6) / 10} h` : `${e.depuis_precedent_min} min`}</small>
                  )}
                </div>
              </div>
            ))}
            <div className="secdash-durees secdash-durees--tl">
              <div><small>Création → affectation</small><strong>{timeline.data.delais_min.creation_affectation == null ? '—' : `${timeline.data.delais_min.creation_affectation} min`}</strong></div>
              <div><small>Affectation → prise</small><strong>{timeline.data.delais_min.affectation_prise == null ? '—' : `${timeline.data.delais_min.affectation_prise} min`}</strong></div>
              <div><small>Prise → résultat</small><strong>{timeline.data.delais_min.prise_resultat == null ? '—' : `${timeline.data.delais_min.prise_resultat} min`}</strong></div>
              <div><small>Résultat → paiement</small><strong>{timeline.data.delais_min.resultat_paiement == null ? '—' : `${timeline.data.delais_min.resultat_paiement} min`}</strong></div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Drill-down recettes : achats détaillés de la cliente sur la fenêtre. */}
      <VentesClienteModal
        open={!!drillCliente}
        onClose={() => setDrillCliente(null)}
        idClient={drillCliente?.id_client}
        nom={drillCliente?.client}
        start={range.start}
        end={range.end}
      />
    </div>
  );
}
